type Indexer = {[key: string]: string | number};
type BaseLocale = {[key: string]: string | BaseLocale};

const expandString = (
  fullKey: string,
  format: string,
  args?: Indexer,
  logWarning?: (msg: string) => void,
): string | undefined => {
  if (!format.includes('{{') && args === undefined) {
    return format;
  }

  const prefixAndOParams = format.split('{{');
  const textParts = prefixAndOParams.map((pp, idx) =>
    idx === 0 ? pp : pp.split('}}')[1],
  );
  const argKeys = prefixAndOParams
    .map((pp, idx) => (idx === 0 ? '' : pp.split('}}')[0]))
    .map(p => (p === '' ? undefined : p))
    .slice(1);

  // logWarning(`textParts: ["${textParts.join('", "')}"]`);
  // logWarning(`params: ["${argKeys.join('", "')}"]`);

  let result = '';
  textParts.forEach((t, i) => {
    result = t !== '' ? result + t : result;
    const argKey = argKeys[i];
    if (argKey) {
      const value = args && args[argKey];
      if (value === undefined) {
        logWarning &&
          logWarning(
            `lookup locale: missing argument ${argKey} for key ${fullKey}`,
          );
      } else {
        result = `${result}${value}`;
      }
    }
  });

  return result === '' ? undefined : result;
};

/**
 * Invariant: remainingKey designates string within localesNode structure
 * @param fullKey
 * @param remainingKey - part of fullKey remaining after navigating a number levels down locales structure
 * @param args - any arguments to be expanded
 * @param localesNode - locale subtree corresponding to navigation so far
 * @param logWarning
 */

const lookupLocaleString = (
  fullKey: string,
  remainingKey: string,
  args: Indexer | undefined,
  localesNode: BaseLocale,
  dot: string,
  logWarning?: (msg: string) => void,
): string | undefined => {
  const subNode = localesNode[remainingKey];
  // Exact match
  if (typeof subNode === 'string') return subNode;

  // If object, must be plural
  if (typeof subNode === 'object') {
    // locales must be plurals
    let count = 0;
    if (args === undefined || args.count === undefined) {
      logWarning &&
        logWarning(`lookup locale: missing plurals for key ${fullKey}`);
    } else if (typeof args.count !== 'number') {
      logWarning &&
        logWarning(
          `lookup locale: Plural key ${fullKey} must have { count: number} as argument`,
        );
    } else {
      count = args.count;
    }

    const countKey = count === 0 ? 'zero' : count === 1 ? 'one' : 'other';
    const pluralValue = subNode[countKey] || subNode['other'];
    if (typeof pluralValue !== 'string') {
      logWarning &&
        logWarning(
          `lookup locale: Cannot resolve locale for plural key ${fullKey} and count: ${count}. Hint: use 'other'-key as fallback`,
        );
      return undefined;
    }
    return pluralValue;
  }

  // Try next level
  const dotIdx = remainingKey.indexOf(dot);
  const childKey = dotIdx > 0 ? remainingKey.substr(0, dotIdx) : undefined;
  const childNode =
    childKey && childKey.length > 0 ? localesNode[childKey] : undefined;
  if (!childNode || typeof childNode !== 'object') {
    return undefined;
  }

  const childRemainingKey = remainingKey.substr(dotIdx + 1);
  return lookupLocaleString(
    fullKey,
    childRemainingKey,
    args,
    childNode,
    dot,
    logWarning,
  );
};

const lookup = (
  key: string,
  args: Indexer | undefined,
  dot: string,
  locales: BaseLocale,
  logWarning?: (msg: string) => void,
): string => {
  const localeString = lookupLocaleString(
    key,
    key,
    args,
    locales,
    dot,
    logWarning,
  );
  if (localeString === undefined) {
    logWarning && logWarning(`Missing translation for key: "${key}"`);
    return `Missing "${key}"`;
  }

  const expaneded = expandString(key, localeString, args, logWarning);

  if (!expaneded) {
    logWarning &&
      logWarning(
        `lookup locale: Could not expand arguments: ${JSON.stringify(
          args,
        )} for key: ${key}`,
      );
    return localeString;
  }

  return expaneded;
};

export {lookup};
